import * as React from "react";
import { useMappedState } from "redux-react-hook";

import { useEventListener } from "src/library/hooks/useEventListener";
import { ActionsPopOut } from "src/library/components/ActionsPopOut/index";
import {
  Button,
  ButtonType
} from "src/library/components/Button";
import { Input } from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import { IProduct } from "src/core/products/api/products.api";
import tokenizeString from "src/library/utils/string-manipulations/tokenize-string";
import { IStore } from "src/core/shared/store";

//
// TODO: Update interface once we know what it is
// TODO: show modal on confirm
// TODO: if multiple messages on date click - show all

interface IListContextProps {
  createList: (name: string) => void;
  loading: boolean;
  product?: IProduct;
  $buttonType?: ButtonType;
}

export const CreateListContext: React.ReactFCWithChildren<IListContextProps> = ({
  createList, loading, $buttonType = "primary"
}) => {
  const { global: { settings } } = useMappedState(({ global }: IStore) => ({ global }));

  const [ position, setPosition ] = React.useState({
    x: 0,
    y: 0
  });

  const [ newListName, setNewListName ] = React.useState<string>("");
  const divRef = React.useRef<HTMLDivElement>(null);
  const [ showAction, setShowAction ] = React.useState(false);

  const toggleShowAction = () => {
    // Open up action dropdown for user:
    setShowAction(!showAction);
  };

  React.useEffect(() => {
    if (showAction) {
      setOverlayPosition(true);
    }
  }, [ showAction ]);

  const setOverlayPosition = (autoScroll = false) => {
    if (divRef.current) {
      const bounds = divRef.current.getBoundingClientRect();
      let right = bounds.right - bounds.width;

      if (window.outerWidth <= 500) {
        right = 0;
      } else if (right + 400 > window.outerWidth) {
        right = bounds.right - 300;
      }

      setPosition({
        y: bounds.top + bounds.height,
        x: right
      });

      if (autoScroll) {
        const scrollWrapper = document.getElementById("main-wrapper");

        if (scrollWrapper) {
          const currentBottom = bounds.top + 350;
          const currentScroll = scrollWrapper.scrollTop;

          if (currentBottom > window.outerHeight) {
            scrollWrapper.scrollTo({
              top: currentScroll + (currentBottom - window.outerHeight),
              behavior: "smooth"
            });
          }
        }
      }
    }
  };

  const handler = React.useCallback(() => {
    setOverlayPosition();
  }, [ divRef ]);

  useEventListener("resize", handler);
  useEventListener("scroll", handler, document.getElementById("main-wrapper"));

  return (
    <>
      <div style={{ position: "relative" }}>
        <Button
          icon={Icons.TABLE}
          $buttonType={$buttonType}
          onClick={toggleShowAction}
          size="medium"
          loading={loading}
        >
          {`Create a new ${settings?.terminology ? tokenizeString("%LIST%", settings.terminology) : "list"}`}
        </Button>

        {showAction && (
          <ActionsPopOut
            {...position}
            action={() => createList(newListName)}
            handleClose={() => {
              toggleShowAction();
              setNewListName("");
            }}
            directionHorizontal="left"
            directionVertical="top"
            steps={[
              {
                title: "Choose a name for your list",
                component: <Input
                  name="listName"
                  label="List name"
                  as="input"
                  handleChange={e => setNewListName(e.target.value)}
                           />,
                padComponent: true,
                allowNext: newListName.length > 0
              }
            ]}
          />
        )}
      </div>
    </>
  );
};
